import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldArray, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useInvitation } from './invitationFunctions';
import { Box, Checkbox, FormControlLabel, IconButton, Typography } from '@mui/material';
import { BASE_URL_WITH_VERSION } from '../../../../utils/api';
import { WorkplaceHandoverConfigRequest } from '@api/models/invitation/WorkplaceHandoverConfigRequest';
import { FormContainer } from '../../../common/form/FormContainer';
import { QcsTypography } from '../../../common/basic/QcsTypography';
import { SupplierEmployeeAutocomplete } from '../../../common/form/SupplierEmployeeAutocomplete';
import { Input } from '../../../common/form/Input';
import { SubmitButton } from '../../../common/form/SubmitButton';
import { EmployeeAutocomplete } from '../../../common/form/EmployeeAutocomplete';
import { selectIdentity, selectIdentityCompanyId } from '../../../../store/entities/identity';
import { useAppSelector } from '../../../../store';
import { selectCompanyCustomization } from '../../../../store/entities/companyCustomization';
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import { SupplierAutocomplete } from '../../../common/form/SupplierAutocomplete';
import Grid from '@mui/system/Grid';
import { DatePicker } from '../../../common/form/DatePicker';
import { QcsButton } from '../../../common/basic/QcsButton';
import { DocumentInput } from '../../../risk/DocumentInput';
import { QcsTable } from '../../../common/basic/QcsTable';
import { QcsTableHead } from '../../../common/basic/QcsTableHead';
import { QcsTableCell } from '../../../common/basic/QcsTableCell';
import { QcsTableBody } from '../../../common/basic/QcsTableBody';
import { QcsTableRow } from '../../../common/basic/QcsTableRow';
import { getMediaToken } from '../../../../utils/storage';
import { ShowImageButton } from './ShowImageButton';
import { getFirstLastNameObj, getLangNameObj } from '../../../../utils/format';
import SignUsersModal from './SignUsersModal';
import { QcsAutocomplete } from '../../../common/form/QcsAutocomplete';
import { useTranslation } from 'react-i18next';
import { hasRole } from '../../../../utils/roles';
import { InvitationState } from '@api/models/enums/InvitationState';
import { QcsTimePicker } from '../../../common/form/QcsTimePicker';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { ActivityDto } from '@api/models/activity/ActivityDto';
import { UserState } from '@api/models/enums/UserState';
import { UserRole } from '@api/models/enums/UserRole';
import { WorkplaceHandoverSectionGroupDto } from '@api/models/settings/WorkplaceHandoverSectionGroupDto';
import { doNothing } from '../../../../utils/shared';
import { companySettingsService, workplaceHandoverService } from '@api/services/services';
import { resources } from '@api/resources';
import { ShowError } from '../../../ShowError';

function HandoverProtocol({ onDirtyChange }: { onDirtyChange?: (dirty: boolean) => void }) {
  const { invitation, t, callWithLock, reloadInvitation } = useInvitation();
  const identity = useAppSelector(selectIdentity);
  const canEdit = hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah, UserRole.ManagerWorkplace]);
  const isArchived = invitation.state === InvitationState.Archived;
  const isDisabled = !canEdit || isArchived;
  const [sections, setSections] = useState<WorkplaceHandoverSectionGroupDto[]>();
  const [disableFooterNames, setDisableFooterNames] = useState(false);
  const [showSignUsersModal, setShowSignUsersModal] = useState(false);
  const [formDirty, setFormDirty] = useState(false);
  const { i18n } = useTranslation();
  const mediaToken = getMediaToken();
  const identityCompanyId = useAppSelector(selectIdentityCompanyId);
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const currentLanguage = companyCustomization?.applicationLanguages?.[0];

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(formDirty);
    }
  }, [formDirty, onDirtyChange]);

  useEffect(() => {
    if (identityCompanyId && sections === undefined) {
      companySettingsService.getWorkplaceHandoverSections().then(({ data }) => {
        setSections(data);
      });
    }
  }, [identityCompanyId, sections]);

  // Prepare initial form values, pre-fill from invitation
  const initialValues = useMemo(
    () => ({
      startOfWork: invitation.workplaceHandover?.startOfWork ?? undefined,
      endOfWork: invitation.workplaceHandover?.endOfWork ?? undefined,
      workingHoursFrom: invitation.workplaceHandover?.workingHoursFrom?.substring(0, 5) ?? undefined,
      workingHoursTo: invitation.workplaceHandover?.workingHoursTo?.substring(0, 5) ?? undefined,
      customerResponsiblePerson: invitation.workplaceHandover?.customerResponsiblePerson ?? null,
      customerHealthSafetyPerson: invitation.workplaceHandover?.customerHealthSafetyPerson ?? null,
      supplierResponsiblePerson: invitation.workplaceHandover?.supplierResponsiblePerson ?? null,
      supplierHealthSafetyPerson: invitation.workplaceHandover?.supplierHealthSafetyPerson ?? null,
      coordinator: invitation.workplaceHandover?.coordinator ?? null,
      coordinatorEmployee: invitation.workplaceHandover?.coordinatorEmployee ?? null,
      subcontractors: invitation.workplaceHandover?.subcontractors?.length
        ? invitation.workplaceHandover.subcontractors?.map((sub: any) => ({
            subcontractor: {
              ...sub?.supplier,
            },
            subcontractorEmployee: {
              ...sub,
            },
          }))
        : [],
      customerRisks: invitation.workplaceHandover?.customerRisks || invitation.workplaceHandover?.customerKnownRisks || '',
      supplierRisks: invitation.workplaceHandover?.supplierRisks || invitation.workplaceHandover?.supplierKnownRisks || '',
      sections: invitation.workplaceHandover?.sections?.filter((s) => s.selected)?.map((s) => s.id) ?? [],
      coordinatorId: invitation.workplaceHandover?.coordinator?.id ?? null,
      coordinatorEmployeeId: invitation.workplaceHandover?.coordinatorEmployee?.id ?? null,
      customerResponsiblePersonId: invitation.workplaceHandover?.customerResponsiblePerson?.id ?? null,
      customerHealthSafetyPersonId: invitation.workplaceHandover?.customerHealthSafetyPerson?.id ?? null,
      supplierResponsiblePersonId: invitation.workplaceHandover?.supplierResponsiblePerson?.id ?? null,
      supplierHealthSafetyPersonId: invitation.workplaceHandover?.supplierHealthSafetyPerson?.id ?? null,
      orderedContractorWork: invitation.workplaceHandover?.orderedContractorWork ?? '',
      activitiesPerformed: invitation.workplaceHandover?.activitiesPerformed ?? [],
      workplaces: invitation.workplaceHandover?.workplaces ?? [],
      subcontractorEmployees: [] as string[],
    }),
    [invitation],
  );

  // Validation schema
  const validationSchema = Yup.object().shape({
    startOfWork: Yup.string().nullable().notRequired(),
    endOfWork: Yup.string()
      .nullable()
      .when('startOfWork', ([validFrom], schema) =>
        schema.test({
          test: (validTo: string | null | undefined) => {
            return !validTo || validFrom <= validTo;
          },
          message: t('errors.dateToBeforeFrom')!,
        }),
      )
      .notRequired(),
    workingHoursFrom: Yup.string().notRequired(),
    workingHoursTo: Yup.string().notRequired(),
    customerResponsiblePerson: Yup.mixed().required(t('errors.required')),
    customerHealthSafetyPerson: Yup.mixed().required(t('errors.required')),
    supplierResponsiblePerson: Yup.mixed().required(t('errors.required')),
    supplierHealthSafetyPerson: Yup.mixed().required(t('errors.required')),
    activitiesPerformed: Yup.array().min(1, t('errors.required')),
    workplaces: Yup.array().min(1, t('errors.required')),
    orderedContractorWork: Yup.mixed().required(t('errors.required')),
    coordinator: Yup.mixed().notRequired(),
    coordinatorEmployee: Yup.mixed().notRequired(),
    subcontractors: Yup.array().of(Yup.object()).notRequired(),
    customerRisks: Yup.string().notRequired(),
    supplierRisks: Yup.string().notRequired(),
  });

  // Save handler
  const handleSubmit = useCallback(
    async (values: typeof initialValues, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
      setFormDirty(false);
      await callWithLock(async () => {
        const request: WorkplaceHandoverConfigRequest = {
          startOfWork: values.startOfWork!,
          endOfWork: values.endOfWork ?? null,
          workingHoursFrom: values.workingHoursFrom || null,
          workingHoursTo: values.workingHoursTo || null,
          orderedContractorWork: values.orderedContractorWork,
          activitiesPerformed: values.activitiesPerformed.map((it) => it.id!),
          workplaces: values.workplaces.map((it) => it.id!),
          customerResponsiblePersonId: values.customerResponsiblePerson!.id,
          customerHealthSafetyPersonId: values.customerHealthSafetyPerson!.id,
          supplierResponsiblePersonId: values.supplierResponsiblePerson!.id,
          supplierHealthSafetyPersonId: values.supplierHealthSafetyPerson!.id,
          coordinatorId: values.coordinator?.id ?? null,
          coordinatorEmployeeId: values.coordinatorEmployee?.id ?? null,
          subcontractorEmployees: values.subcontractors?.map((sub) => sub.subcontractorEmployee!.id) ?? [],
          customerRisks: values.customerRisks ?? null,
          supplierRisks: values.supplierRisks ?? null,
          sections: values.sections,
        };

        await workplaceHandoverService.updateWorkplaceHandover(invitation.id!, request);
        await reloadInvitation();
      });
      setSubmitting(false);
    },
    [callWithLock, invitation.id, reloadInvitation],
  );

  return (
    <>
      <FormContainer css={{ width: '100%', maxWidth: 'unset' }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
          onReset={() => {
            setFormDirty(false);
          }}
        >
          {({ values, setFieldValue, isSubmitting, dirty, submitForm, validateForm }) => {
            if (formDirty !== dirty) {
              setFormDirty(dirty);
            }
            return (
              <Form>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Input
                      disabled={isDisabled}
                      name="orderedContractorWork"
                      label={t('invitation.orderedContractWork')}
                      multiline
                      fullWidth
                      required
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <QcsAutocomplete
                      disabled={isDisabled}
                      multiple={true}
                      name={'activitiesPerformed'}
                      label={t?.('invitation.activitiesPerformed')}
                      required
                      options={invitation?.activities?.map((it) => it.activity!) ?? []}
                      getOptionId={(option: ActivityDto) => option.id!}
                      getOptionName={(option: ActivityDto) => getLangNameObj(i18n, option)}
                      loading={false}
                      loadingError={false}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <QcsAutocomplete
                      disabled={isDisabled}
                      multiple={true}
                      name={'workplaces'}
                      label={t?.('invitation.selectedWorkplace')}
                      required
                      options={invitation?.workplaces?.map((it) => it.workplace!) ?? []}
                      getOptionId={(option: ReferenceDto) => option.id}
                      getOptionName={(option: ReferenceDto) => getLangNameObj(i18n, option)}
                      loading={false}
                      loadingError={false}
                    />
                  </Grid>
                  <Grid
                    size={12}
                    display="flex"
                    sx={{
                      flexDirection: {
                        xs: 'column',
                        sm: 'row',
                      },
                      gap: 2,
                      alignItems: {
                        xs: 'flex-start',
                        lg: 'center',
                      },
                    }}
                  >
                    <DatePicker
                      disabled={isDisabled}
                      label={t?.('invitation.startOfWork')}
                      name="startOfWork"
                      css={{ backgroundColor: '#fff', minWidth: '200px' }}
                    />
                    <DatePicker
                      disabled={isDisabled}
                      label={t?.('invitation.endOfWork')}
                      name="endOfWork"
                      css={{ backgroundColor: '#fff', minWidth: '200px' }}
                    />
                    <QcsTimePicker disabled={isDisabled} label={t('handoverSettings.workingHoursFrom')} name="workingHoursFrom" />
                    <QcsTimePicker disabled={isDisabled} label={t('handoverSettings.workingHoursTo')} name="workingHoursTo" />
                  </Grid>
                  <Grid size={{ xs: 12, lg: 6 }}>
                    <Typography variant="subtitle2">{t?.('invitation.customerResponsible')}</Typography>
                  </Grid>
                  <Grid
                    size={{ xs: 12, lg: 6 }}
                    sx={{
                      display: {
                        xs: 'none',
                        lg: 'flex',
                      },
                    }}
                  >
                    <Typography variant="subtitle2">{t('invitation.supplierResponsible')}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <EmployeeAutocomplete
                      disabled={isDisabled}
                      name="customerResponsiblePerson"
                      label={t?.('invitation.customerResponsiblePerson')}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <EmployeeAutocomplete
                      disabled={isDisabled}
                      required
                      name="customerHealthSafetyPerson"
                      label={t?.('invitation.customerHealthSafetyPerson')}
                      allowedRoles={[UserRole.ManagerOsah, UserRole.ManagerWorkplace]}
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, lg: 6 }}
                    sx={{
                      display: {
                        xs: 'flex',
                        lg: 'none',
                      },
                    }}
                  >
                    <Typography variant="subtitle2">{t?.('invitation.supplierResponsible')}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <SupplierEmployeeAutocomplete
                      disabled={isDisabled}
                      name="supplierResponsiblePerson"
                      label={t?.('invitation.supplierResponsiblePerson')}
                      supplierId={invitation.supplier?.id}
                      role={UserRole.ExternalManager}
                      state={[UserState.Active, UserState.Invited]}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                    <SupplierEmployeeAutocomplete
                      disabled={isDisabled}
                      name="supplierHealthSafetyPerson"
                      label={t?.('invitation.supplierHealthSafetyPerson')}
                      supplierId={invitation.supplier?.id}
                      role={UserRole.ExternalManager}
                      state={[UserState.Active, UserState.Invited]}
                      required
                    />
                  </Grid>

                  <Grid size={12}>
                    <Typography variant="subtitle2">{t?.('invitation.coordinator')}</Typography>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6, lg: 5 }}>
                    <SupplierAutocomplete
                      disabled={isDisabled}
                      name="coordinator"
                      label={t?.('invitation.coordinator')}
                      onChange={() => {
                        setFieldValue('coordinatorEmployee', null);
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6, lg: 5 }}>
                    <SupplierEmployeeAutocomplete
                      disabled={isDisabled}
                      supplierId={values.coordinator?.id}
                      name="coordinatorEmployee"
                      label={t?.('invitation.coordinatorEmployee')}
                    />
                  </Grid>

                  <Grid size={12}>
                    <Typography variant="subtitle2" mb={1} mt={1}>
                      {t?.('invitation.customerKnownRisksTitle')}
                    </Typography>
                    <Input disabled={isDisabled} name="customerRisks" label={t?.('invitation.customerKnownRisks')} multiline fullWidth />
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="subtitle2" mb={1} mt={1}>
                      {t?.('invitation.supplierKnownRisksTitle')}
                    </Typography>
                    <Input disabled={isDisabled} name="supplierRisks" label={t?.('invitation.supplierKnownRisks')} multiline fullWidth />
                  </Grid>

                  {/* Row 6: Subcontractors Section */}
                  <Grid size={12}>
                    <Box mt={2}>
                      <Typography variant="subtitle2" mb={2}>
                        {t?.('invitation.subcontractors') ?? 'Subcontractors'}
                      </Typography>
                      <FieldArray name="subcontractors">
                        {(arrayHelpers) => (
                          <div>
                            {values.subcontractors && values.subcontractors.length > 0 ? (
                              values.subcontractors.map((sub, idx) => (
                                <Grid container display="flex" gap={2} alignItems="center" key={idx} mt={1}>
                                  <Grid size={{ xs: 12, md: 4 }}>
                                    <SupplierAutocomplete
                                      disabled={isDisabled}
                                      name={`subcontractors.${idx}.subcontractor`}
                                      label={t?.('invitation.subcontractor')}
                                      onChange={() => {
                                        setFieldValue(`subcontractors.${idx}.subcontractorEmployee`, null);
                                      }}
                                    />
                                  </Grid>
                                  <Grid size={{ xs: 12, md: 4 }}>
                                    <SupplierEmployeeAutocomplete
                                      disabled={isDisabled}
                                      supplierId={sub.subcontractor?.id}
                                      name={`subcontractors.${idx}.subcontractorEmployee`}
                                      label={t?.('invitation.subcontractorEmployee')}
                                    />
                                  </Grid>
                                  <Grid size={{ xs: 12, md: 2 }}>
                                    {canEdit && !isArchived && (
                                      <IconButton aria-label="remove" onClick={() => arrayHelpers.remove(idx)}>
                                        <RemoveCircleOutline />
                                      </IconButton>
                                    )}
                                    {idx === values.subcontractors.length - 1 && canEdit && !isArchived && (
                                      <IconButton
                                        aria-label="remove"
                                        disabled={!sub.subcontractorEmployee?.id}
                                        onClick={() =>
                                          arrayHelpers.push({
                                            subcontractor: {},
                                            subcontractorEmployee: {},
                                          })
                                        }
                                      >
                                        <AddCircleOutline />
                                      </IconButton>
                                    )}
                                  </Grid>
                                </Grid>
                              ))
                            ) : (
                              <QcsTypography variant="body2">
                                {t?.('invitation.noSubcontractors')}{' '}
                                {canEdit && !isArchived && (
                                  <IconButton
                                    aria-label="remove"
                                    onClick={() =>
                                      arrayHelpers.push({
                                        subcontractor: {},
                                        subcontractorEmployee: {},
                                      })
                                    }
                                  >
                                    <AddCircleOutline />
                                  </IconButton>
                                )}
                              </QcsTypography>
                            )}
                          </div>
                        )}
                      </FieldArray>
                    </Box>
                  </Grid>

                  {/* Row 7: Text Blocks Section */}
                  <Grid size={12}>
                    <Box mt={2}>
                      <QcsTypography variant="subtitle2">{t('invitation.textBlocks')}</QcsTypography>

                      <Box display="flex" flexDirection="column" gap={1}>
                        {sections?.map((block) => (
                          <FormControlLabel
                            key={block.id}
                            onClick={(e) => e.preventDefault()}
                            control={
                              <Checkbox
                                disabled={isDisabled}
                                checked={values.sections?.includes(block.id)}
                                onClick={() => {
                                  const checked = values.sections?.includes(block.id);
                                  if (checked) {
                                    setFieldValue(
                                      'sections',
                                      values.sections?.filter((s) => s !== block.id),
                                    );
                                  } else {
                                    setFieldValue('sections', [...(values.sections || []), block.id]);
                                  }
                                }}
                                name="sections"
                                value={block.id}
                              />
                            }
                            label={block.sections?.find((s) => s.language === currentLanguage)?.title ?? block.id}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>

                  {/* Row 8: Save Button */}
                  <Grid size={12}>
                    <Box mt={3} gap={2} display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
                      {canEdit && !isArchived && (
                        <Box>
                          <SubmitButton
                            variant="outlined"
                            text={t('common.save')}
                            resourceLoading={resources.updateWorkplaceHandover}
                            disabled={isSubmitting}
                          />
                        </Box>
                      )}

                      {invitation.workplaceHandover?.id && (
                        <>
                          {canEdit && !isArchived && (
                            <Box>
                              <QcsButton
                                variant="outlined"
                                onClick={async () => {
                                  const errors = await validateForm();
                                  if (Object.keys(errors).length > 0) return;
                                  await submitForm();
                                  setShowSignUsersModal(true);
                                }}
                                fullWidth={true}
                              >
                                {t('invitation.sign')}
                              </QcsButton>
                            </Box>
                          )}

                          {canEdit && !isArchived && (
                            <Box>
                              <DocumentInput
                                disabled={false}
                                forceEnabled
                                onRemoveOldFile={doNothing}
                                onFileUploaded={(d) => {
                                  workplaceHandoverService.uploadWorkplaceHandoverDocument(invitation.id!, { documentId: d.id! }).then(() => {
                                    reloadInvitation();
                                  });
                                }}
                                label={t('invitation.uploadHandoverProtocolDocument')}
                                asButton
                                fullWidth={true}
                              />
                            </Box>
                          )}
                          <Box display="flex" gap={2} flexDirection="column" width={{ xs: '100%', sm: 'auto' }}>
                            <QcsButton
                              variant="outlined"
                              onClick={async () => {
                                if (canEdit && !isArchived) {
                                  const errors = await validateForm();
                                  if (Object.keys(errors).length > 0) return;
                                  await submitForm();
                                }
                                workplaceHandoverService.exportWorkplaceHandover(invitation.id!, disableFooterNames).then((response) => {
                                  const blob = new Blob([response.data], {
                                    type: 'application/pdf',
                                  });
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `HandoverProtocol_${invitation.id}.pdf`;
                                  a.click();
                                });
                              }}
                              fullWidth={true}
                            >
                              {t('invitation.exportPdf')}
                            </QcsButton>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={disableFooterNames}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setDisableFooterNames(checked);
                                  }}
                                />
                              }
                              label={t?.('invitation.disableFooterNames')}
                              sx={{
                                ml: 0,
                                width: { xs: '100%', sm: 'auto' },
                                alignItems: { xs: 'flex-start', sm: 'center' },
                              }}
                            />
                          </Box>
                        </>
                      )}
                    </Box>
                    <ShowError resource={resources.updateWorkplaceHandover} />
                  </Grid>
                </Grid>
                <SignUsersModal
                  onClose={() => setShowSignUsersModal(false)}
                  open={showSignUsersModal}
                  users={[
                    {
                      id: invitation.employee?.id || '',
                      name: getFirstLastNameObj(invitation.workplaceHandover?.customerResponsiblePerson),
                    },
                    {
                      id: invitation.responsiblePerson?.id || '',
                      name: getFirstLastNameObj(invitation.workplaceHandover?.supplierResponsiblePerson),
                    },
                    ...(values?.coordinatorEmployee
                      ? [
                          {
                            id: values.coordinatorEmployee.id || '',
                            name: getFirstLastNameObj(values.coordinatorEmployee),
                            skippable: true,
                          },
                        ]
                      : []),
                  ]}
                  onSign={(data) => {
                    const formData = new FormData();
                    data.forEach((sign) => {
                      if (sign.file) {
                        if (sign.userId === invitation.employee?.id) {
                          formData.append('clientSignature', sign.file);
                        } else if (sign.userId === invitation.responsiblePerson?.id) {
                          formData.append('contractorSignature', sign.file);
                        } else if (sign.userId === values?.coordinatorEmployee?.id && sign.file.size > 100) {
                          formData.append('coordinatorSignature', sign.file);
                        }
                      }
                    });
                    workplaceHandoverService.uploadSignatures(invitation.id!, formData).then(() => {
                      reloadInvitation();
                    });
                  }}
                />
              </Form>
            );
          }}
        </Formik>
      </FormContainer>
      {invitation.workplaceHandover?.documents && invitation.workplaceHandover.documents.length > 0 && (
        <Box mt={2}>
          <QcsTable>
            <QcsTableHead>
              <QcsTableCell>{t('document.name')}</QcsTableCell>
              <QcsTableCell>{t('invitation.log.timestamp')}</QcsTableCell>
              <QcsTableCell align="right"> </QcsTableCell>
            </QcsTableHead>
            <QcsTableBody>
              {invitation.workplaceHandover?.documents.map((doc) => (
                <QcsTableRow key={doc.document?.id}>
                  <QcsTableCell>
                    <QcsTypography variant="body2">{doc.document?.fileName || ''}</QcsTypography>
                  </QcsTableCell>
                  <QcsTableCell>
                    <QcsTypography variant="body2">
                      {doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : ''}{' '}
                      {doc.uploadedAt ? new Date(doc.uploadedAt + 'Z').toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                    </QcsTypography>
                  </QcsTableCell>
                  <QcsTableCell align="right">
                    <ShowImageButton
                      tooltipTitle={t('invitation.showHandoverProtocolDocument')}
                      documentUrl={`${BASE_URL_WITH_VERSION}${doc.document?.path}?token=${mediaToken}`}
                      fileName={doc.document?.fileName || ''}
                      color="success"
                    />
                  </QcsTableCell>
                </QcsTableRow>
              ))}
            </QcsTableBody>
          </QcsTable>
        </Box>
      )}
    </>
  );
}

export default HandoverProtocol;
